import { useState, useEffect } from "react";

interface ProcessingIndicatorProps {
    workerName: string;
    processingMessage?: string | null;
}

const defaultMessages = [
    "กำลังคิด...",
    "กำลังคิดขั้นตอนต่อ...",
    "กำลังคิดนอกกรอบ...",
    "กำลังคิดคำที่ถูกต้อง...",
    "กำลังคิดอย่างรอบคอบ...",
    "กำลังคิดถึงความเป็นไปได้...",
    "กำลังคิดอย่างหนัก...",
    "กำลังคิดอย่างวิเคราะห์...",
    "กำลังคิดคำที่ชาญฉลาด...",
    "กำลังคิดถึงคำขอของคุณ...",
    "กำลังคิดคำที่สมบูรณ์...",
    "กำลังคิดอย่างหลากหลาย...",
    "กำลังคิดอย่างตรรกะ...",
    "กำลังคิดอย่างชาญฉลาด...",
    "กำลังคิดอย่างสร้างสรรค์...",
    "กำลังประมวลผลข้อมูล...",
    "กำลังเรียบเรียงคำตอบ...",
    "กำลังคิดอย่างเป็นระบบ...",
    "กำลังวิเคราะห์บริบท...",
    "กำลังรวบรวมข้อมูล...",
    "กำลังคิดอย่างละเอียด...",
    "กำลังพิจารณาทุกมุมมอง...",
    "กำลังหาคำตอบที่ดีที่สุด...",
    "กำลังคิดอย่างมีเหตุผล...",
    "กำลังเชื่อมโยงข้อมูล...",
    "กำลังตรวจสอบความถูกต้อง...",
    "กำลังสรุปคำตอบ...",
    "กำลังคิดอย่างถี่ถ้วน...",
    "กำลังปรับแต่งคำตอบ...",
    "กำลังคิดอย่างครบถ้วน...",
];

export default function ProcessingIndicator({
    workerName,
    processingMessage,
}: ProcessingIndicatorProps) {
    const [messageIndex, setMessageIndex] = useState(0);
    const [displayText, setDisplayText] = useState("");
    const [isDeleting, setIsDeleting] = useState(false);

    // Animate fixed processingMessage with typing (restart on change)
    useEffect(() => {
        if (!processingMessage) return;
        const full = processingMessage;
        let i = 0;
        setIsDeleting(false);
        setDisplayText("");
        let timer: ReturnType<typeof setTimeout> | null = null;

        const step = () => {
            i += 1;
            if (i > full.length) return;
            setDisplayText(full.slice(0, i));
            timer = setTimeout(step, 30);
        };

        timer = setTimeout(step, 30);
        return () => {
            if (timer) clearTimeout(timer);
        };
    }, [processingMessage]);

    // Default "is thinking..." typing/deleting loop (only when no processingMessage)
    useEffect(() => {
        if (processingMessage) return;
        const currentWord = defaultMessages[messageIndex];
        const typingSpeed = isDeleting ? 30 : 50; // Speed of typing and deleting

        const timeout = setTimeout(() => {
            if (!isDeleting && displayText === currentWord) {
                // Wait at the end of the word before deleting
                setTimeout(() => setIsDeleting(true), 1500);
            } else if (isDeleting && displayText === "") {
                // Move to next word when fully deleted
                setIsDeleting(false);
                setMessageIndex((prev) => {
                    let nextIndex;
                    do {
                        nextIndex = Math.floor(Math.random() * defaultMessages.length);
                    } while (nextIndex === prev);
                    return nextIndex;
                });
            } else {
                // Add or remove characters
                const nextText = isDeleting
                    ? currentWord.substring(0, displayText.length - 1)
                    : currentWord.substring(0, displayText.length + 1);

                setDisplayText(nextText);
            }
        }, typingSpeed);

        return () => clearTimeout(timeout);
    }, [displayText, isDeleting, messageIndex, processingMessage]);

    const currentName = workerName || "Aera";

    return (
        <div className="flex justify-start">
            <div className="grid items-center justify-start gap-2">
                <span className="text-xs font-semibold text-aera-charcoal">
                    {workerName}
                </span>
                <div className="flex gap-1 items-center animate-pulse">
                    <span className="text-xs text-aera-charcoal mr-1 transition-none border-r-2 border-aera-charcoal pr-1 animate-[blink_1s_infinite]">
                        {currentName} {displayText}
                    </span>
                    <div className="w-2 h-2 bg-aera-amber rounded-full animate-bounce"></div>
                    <div
                        className="w-2 h-2 bg-aera-amberlight rounded-full animate-bounce"
                        style={{ animationDelay: "0.2s" }}
                    ></div>
                    <div
                        className="w-2 h-2 bg-aera-amber rounded-full animate-bounce"
                        style={{ animationDelay: "0.5s" }}
                    ></div>
                </div>
            </div>
        </div>
    );
}
